import { INavigationState } from '../layout/navigation/state/navigation.model';

export const FEATURE_NAME = 'coreFeature';

export interface IPolCoreState {
  userState: IUserState;
  navigationState: INavigationState;
  providerState: IProviderState;
}

export interface IUserState {
  user: IUser;
  refreshToken: IRefreshToken;
  myOaas: IMyOaasResponse;
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  idleTimeoutResetCount: number;
  shouldDisableUserMenu: boolean;
}

export interface IRefreshToken {
  isRefreshInProgress: boolean;
  url: string;
  resetInactivityTime?: boolean;
}

export interface IUser {
  username: string;
  email?: string;
  firstName: string;
  lastName: string;
  providerType: string;
  middleInitial?: string;
  phoneNumber?: string;
  phoneNumberExtension?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  isPhysician?: boolean;
  npiNumber?: string;
  id?: string;
  providerFavorites?: IProvider[];
  isInternalUser?: boolean;
}

export interface IPOLUserProfileResponse {
  providerType: string;
}


export interface IUserAgreement {
  documentUrl: string;
  signatureRequired: boolean;
}

export interface IPOLUserResponse {
  userProfile: IPOLUserProfileResponse
  useAgreement: IUserAgreement;

}

// TODO: Move these interfaces to their own modules
export interface ICoverage {
  accumulatorType: string;
  benefitLevels: any[];
  billType: string;
  cdhs: any;
  coc: string;
  coinsurance: string;
  companyCode: string;
  consumerConnectId: string;
  copay: any;
  corpId: string;
  coverageDescription1: string;
  coverageGroupType: string;
  deductible: string;
  displayPlanCodeDescription: string;
  eligibilityStatus: string;
  eligibilityTypeDescription: string;
  endDate: string;
  entity: string;
  groupAddress: any;
  groupAgePolicy: string;
  groupAssociationCode: string;
  groupName: string;
  groupNumber: string;
  hasAnyDental: boolean;
  hasAnyVision: boolean;
  hasCdh: boolean;
  hasDentalAdvantage: boolean;
  hasDentalDiscount: boolean;
  hasDominionDentalPediatric: boolean;
  hasHsaOnly: boolean;
  hasMedical: boolean;
  hasNva: boolean;
  hasPediatric: boolean;
  hasRx: boolean;
  hasSantechDentalPediatric: boolean;
  hasValueAddedBenefits: boolean;
  hasVisionCare: boolean;
  hixFamilyId: string;
  hixMemberId: string;
  hixPolicyId: string;
  hixSource: string;
  incentiveTypeDescription: string;
  incentiveTypeId: string;
  isAso: boolean;
  isDigitalFirstMember: boolean;
  isMostRecent: boolean;
  memberRiders: any;
  mhLimits: string;
  otherBenefits: any;
  otherEligibilities: any[];
  outOfPocketAccumulatorType: string;
  pcpRequired: string;
  pensionAmt: number;
  planBenefits: any;
  planCode: string;
  planCodeDescription: string;
  planEndDate: string;
  planStartDate: string;
  planType: string;
  planTypeDescription: string;
  preExistingConditionFlag: string;
  preExistingConditionFromDte: number;
  preExistingConditionThruDte: number;
  premiumOverrideAmt: number;
  printOnIdCard: boolean;
  qualHealthPlanId: string;
  riderOverrideAmt4: number;
  riders: string;
  shopEin: string;
  startDate: string;
  subGroupCategory: string;
  subGroupCategoryName: string;
  subGroupName: string;
  subGroupNumber: string;
  terminationReason: string;
  transitionEffectiveDate: number;
  wellnessLevelDescription: string;
  wellnessLevelId: string;
}
export interface IAddUserSearchResponse<ResponseType> {
  results: ResponseType;
}

export interface IRoleLandingPageEachRoleFeatureDetails {
  roleLabel: string;
  hasFeatures: boolean;
}


// favorite-providers, search-result-list, etc. UI interface
export interface IProvider {
  providerId?: string;
  providerName?: string;
  npi?: string;
  providerUserName?: string;
  fullName?: string;
  providerRole?: string;
  type?: string;
  details?: string;
  value?: string;
  name?: string;
  subTypeDesc?: string;
  providerRoles?: string[];
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  zipCode?: string;
  vCodes?: IVCodes[];
  countryName?: string;
  data?: string;
}

// provider-search-suggestive endpoint response
export interface IProviderSearch {
  providerNPI: string;
  providerFullName: string;
  billingProviderId?: string;
  taxIdentificationNumber?: string;
  sourceProviderNumber: string;
  subType: string;
  address?: {
    addressLine1: string;
    city: string;
    stateAbbreviation: string;
    countryName: string;
    zipCode: string;
    countyName: string;
  };
}

export interface IProviderState {
  favoriteProviders?: IProvider[];
  loading?: boolean;
  selectedProvider?: IProvider | null;
  providerSearchResults?: IProvider[];
  clearProviderSearchResults: boolean;
}

export interface IMyOaaUser {
  firstName: string;
  lastName: string;
  email: string;
  securityGroupId: string;
  securityGroupName?: string;
  taxIDs: IMyOaaSecurityGroupTaxId[];
  permissions: string[][];
  taxIdCount?: number;
}

export interface IMyOaaSecurityGroupTaxId {
  taxId: string;
  name?: string;
  viewMore?: boolean;
  vCodes: IOaaSecurityGroupTaxIdVcode[];
}

export interface IOaaSecurityGroupTaxIdVcode {
  name: string;
  number: string;
}

export interface IMyOaaSecurityGroup {
  id: string;
  name: string;
  taxIDs: IMyOaaSecurityGroupTaxId[];
  permissions: string[];
  taxIdCount?: number;
  viewMore?: boolean;
}

export interface IMyOaasResponse {
  oaaUsers: IMyOaaUser[];
  securityGroups: IMyOaaSecurityGroup[];
}

export interface IProviderPreferenceResponse {
  results: IProvider[];
}

export interface ISuggestiveSearchResponse {
  results: ISuggestiveSearch[];
  hasMore: boolean;
}

export interface IVCodes {
  vCode: string;
  isAuthorized: boolean;
}

export interface ISuggestiveSearch {
  providerNPI: string;
  displayProviderNPI?: string;
  providerFullName: string;
  sourceProviderNumber: string;
  subType: string;
  subPartNPIs?: string[];
  officeId?: string;
  officeName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  zipCode?: string;
  vCodes?: IVCodes[];
  taxIdentificationNumber?: string;
}
